import axios from "axios";
import { DocumentClassification } from "@/api/documentClassification";

export interface Attachment {
  id: string | null;
  name: string | null;
  sizeByte: number | null;
  contentAsBase64: string | null;
  mimeType: string | null;
  uploadedAtUtc: Date;
  contactRef?: string;
  documentClassification?: DocumentClassification | null;
}

export enum UploadStatus {
  BLACKLISTED = "BLACKLISTED",
  EXIST = "EXIST",
  EMPTY = "EMPTY",
  STORAGE_FAILED = "STORAGE_FAILED",
  FAILED = "FAILED",
  OK = "OK",
}

interface AttachmentApi {
  list(contactId: string): Promise<Attachment[]>;
  count(contactId: string): Promise<number>;
  getById(id: string): Promise<Attachment>;
  add(contactId: string, file: File): Promise<UploadStatus>;
  delete(contactId: string, id: string): Promise<void>;
  move(contactId: string, id: string, newContactId: string): Promise<void>;
  generateDownloadLink(contactId: string, id: string): string;
  rename(contactId: string, id: string, name: string): Promise<void>;
  changeClassification(contactId: string, id: string, classification: DocumentClassification): Promise<void>;
  predefinedAttachments(): Promise<Attachment[]>;
}

export const attachmentApi: AttachmentApi = {
  async count(contactId) {
    return (await axios.get(`/api/contacts/${contactId}/attachments/_count`))
      .data;
  },
  async list(contactId) {
    const result = (await axios.get(`/api/contacts/${contactId}/attachments`))
      .data as Attachment[];
    if (result) {
      return result.map(
        (v) =>
          ({
            ...v,
            uploadedAtUtc: v.uploadedAtUtc ? new Date(v.uploadedAtUtc) : null,
          } as Attachment)
      );
    }
    return [];
  },

  async getById(id) {
    const result = (await axios.get(`/api/contacts/attachments/${id}`)).data;
    return {
      ...result,
      uploadedAtUtc: result.uploadedAtUtc
        ? new Date(result.uploadedAtUtc)
        : null,
    };
  },

  async add(contactId, file) {
    const formData = new FormData();
    formData.append("file", file);
    return (await axios.post(`/api/contacts/${contactId}/attachments`, formData)).data;
  },

  async delete(contactId, id) {
    return await axios.delete(`/api/contacts/${contactId}/attachments/${id}`);
  },

  async move(contactId, id, newContactId) {
    return await axios.put(`/api/contacts/${contactId}/attachments/${id}/move/${newContactId}`);
  },

  generateDownloadLink(contactId: string, id: string): string {
    return `/api/contacts/${contactId}/attachments/${id}`;
  },

  async rename(contactId, id, name) {
    await axios.put(
      `/api/contacts/${contactId}/attachments/${id}/rename`,
      `{ "newDocumentName": "${name}" }`,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  },
  async changeClassification(contactId, id, classification) {
    await axios.put(
      `/api/contacts/${contactId}/attachments/${id}/reclassification`,
      `{ "newClassificationId": "${classification.id}" }`,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  },
  async predefinedAttachments() {
    return (await axios.get(`/api/contacts/attachments/_predefined`))
        .data as Attachment[];
  }
};
